import { Pipe, PipeTransform } from '@angular/core';

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Inline markdown: code spans are protected first so their content is not touched by the other rules. */
const renderInline = (text: string): string => {
  const codeSpans: string[] = [];
  const withPlaceholders = text.replace(/`([^`\n]+)`/g, (_, code: string) => {
    codeSpans.push(`<code>${code}</code>`);
    return `\u0000${codeSpans.length - 1}\u0000`;
  });
  const rendered = withPlaceholders
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>')
    .replace(/(^|[^*\w])\*([^*\n]+?)\*(?=[^*\w]|$)/g, '$1<em>$2</em>')
    .replace(/(^|[^_\w])_([^_\n]+?)_(?=[^_\w]|$)/g, '$1<em>$2</em>')
    .replace(
      /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );
  return rendered.replace(/\u0000(\d+)\u0000/g, (_, index: string) => codeSpans[Number(index)]);
};

type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'paragraph'; lines: string[] };

const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;

const parseBlocks = (markdown: string): Block[] => {
  const blocks: Block[] = [];
  const last = (): Block | undefined => blocks[blocks.length - 1];

  for (const rawLine of markdown.replace(/\r\n?/g, '\n').split('\n')) {
    const line = rawLine.trimEnd();
    if (!line.trim()) {
      // A blank line closes the current block; the next text starts a new one.
      if (last()?.kind === 'paragraph' && (last() as { lines: string[] }).lines.length === 0) continue;
      blocks.push({ kind: 'paragraph', lines: [] });
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      blocks.push({ kind: 'heading', level: heading[1].length, text: heading[2] });
      continue;
    }
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    if (bullet || numbered) {
      const ordered = !!numbered;
      const item = (bullet ?? numbered)![1];
      const current = last();
      if (current?.kind === 'list' && current.ordered === ordered) current.items.push(item);
      else blocks.push({ kind: 'list', ordered, items: [item] });
      continue;
    }
    const current = last();
    if (current?.kind === 'paragraph') current.lines.push(line.trim());
    else blocks.push({ kind: 'paragraph', lines: [line.trim()] });
  }
  return blocks.filter((block) => block.kind !== 'paragraph' || block.lines.length > 0);
};

/**
 * Renders the subset of markdown the assistant uses (headings, bullet / numbered lists, bold, italic,
 * inline code, http links, paragraphs) to HTML. The input is HTML-escaped first, so only the tags
 * produced here reach the template; Angular sanitizes the result again on `[innerHTML]`.
 */
export const renderAgentMarkdown = (markdown: string): string =>
  parseBlocks(escapeHtml(markdown))
    .map((block) => {
      switch (block.kind) {
        case 'heading':
          return `<h${block.level}>${renderInline(block.text)}</h${block.level}>`;
        case 'list': {
          const tag = block.ordered ? 'ol' : 'ul';
          return `<${tag}>${block.items.map((item) => `<li>${renderInline(item)}</li>`).join('')}</${tag}>`;
        }
        case 'paragraph':
          return `<p>${block.lines.map(renderInline).join('<br>')}</p>`;
      }
    })
    .join('');

@Pipe({ name: 'agentMarkdown' })
export class AgentMarkdownPipe implements PipeTransform {
  transform(markdown: string | null | undefined): string {
    return markdown ? renderAgentMarkdown(markdown) : '';
  }
}
